import { Navigate, useLocation } from 'react-router-dom';
import ProtectedRoute from '@/components/ProtectedRoute';
import { RouteGateFallback } from '@/components/routing/RouteGateFallback';
import { Button } from '@/components/primitives/Button';
import { useCreatorAccess } from '@/hooks/useCreatorAccess';

/** Router state My programs reads to open the Create upsell once. */
export interface CreateUpsellRouteState {
  createUpsell: { from: string };
}

/**
 * Every /create/program route checks create_programs through
 * get_my_creator_access.can_create_powerprompt. No flag changes this.
 * Members who cannot create go to My programs, which opens the upsell. A
 * first check that fails never shows the editor: it offers a retry instead.
 * A background recheck that fails keeps the last good answer, so an open
 * editor never loses unsaved work (the server still checks every save).
 */
function ProgramAuthoringGate({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const access = useCreatorAccess();

  if (access.isLoading) return <RouteGateFallback />;

  if (access.isError && !access.data) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-jp-bg-page px-4 text-jp-fg-primary">
        <div role="alert" className="grid max-w-sm justify-items-center gap-4 text-center">
          <p className="font-jp-body text-base text-jp-fg-secondary">
            We couldn't check your account, so the editor didn't open.
          </p>
          <Button variant="primary" onClick={() => void access.refetch()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (!access.canAuthor) {
    const state: CreateUpsellRouteState = {
      createUpsell: { from: `${location.pathname}${location.search}` },
    };
    return <Navigate to="/my-programs" replace state={state} />;
  }

  return <>{children}</>;
}

export function ProgramAuthoringRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <ProgramAuthoringGate>{children}</ProgramAuthoringGate>
    </ProtectedRoute>
  );
}

export default ProgramAuthoringRoute;
